/**
 * Class description
 *
 * Created: 2018-09-13
 * @author jonas
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {ReelAnimationState} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelAnimationState";
import {IReelStartSpinData, IReelStopSpinData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {SpinReelController} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TimelineLite} from "gsap";
import {IAdjacentBetLineWinData} from "../../utils/SymbolUtil";
import {DirtyWildGameModuleConfig} from "../DirtyWildGameModuleConfig";
import {DirtyWildsGameConfig} from "../DirtyWildsGameConfig";
import {DirtyWildsSoundConfig} from "../DirtyWildsSoundConfig";
import {DirtyWildsRandomFeatureEvent} from "../features/DirtyWildsRandomFeatureReelStopController";
import {DirtyWildGameMode} from "../features/DirtyWildsRespinZeroBetController";
import {DirtyWildsRFSyncEvent} from "../features/DirtyWildsRFSyncController";
import {DirtyWildsModeData} from "../server/data/DirtyWildsModeData";
import {DirtyWildsParsedGameData} from "../server/data/DirtyWildsParsedGameData";
import {DirtyWildsParsedInitData} from "../server/data/DirtyWildsParsedInitData";
import {DirtyWildsStateReelSymbol, DirtyWildsSymbols} from "../symbols/DirtyWildsStateReelSymbol";
import {DirtyWildReelPartAnimator} from "./DirtyWildReelPartAnimator";
import {DirtyWildReelView} from "./DirtyWildReelView";
import {DirtyWildsRealAreaEvent} from "./DirtyWildsReelAreaView";

export class DirtyWildsReelController extends SpinReelController {
    public static ADD_SPIN_START_DELAY:string = "DirtyWildsReelController_addSpinStartDelay";

    private _randomDelay:number[] = [0, 0.5, 1];
    private _randomFeatureStopDelays:number[] = [
        this._randomDelay[0],
        this._randomDelay[1],
        this._randomDelay[2],
        -this._randomDelay[2],
        -(this._randomDelay[2] + this._randomDelay[1]),
    ];

    private _randomQuickDelay:number[] = [0, 0, 0];
    private _randomFeatureQuickStopDelays:number[] = [
        this._randomQuickDelay[0],
        this._randomQuickDelay[1],
        this._randomQuickDelay[2],
        -this._randomQuickDelay[2],
        -(this._randomQuickDelay[2] + this._randomQuickDelay[1]),
    ];
    private _reSpinStopDelays:number[] = [0, 2.4, 2.4, 2.4, -10.3]; // reel 0 and 1 don't need to have longer spin.

    private _slowMoActive:boolean = false;
    private _slowMoFadeOutDuration:number = 0;
    public _isRandomSyncFeatureTriggered:boolean = false;
    public _isRandomFeatureTriggered:boolean = false;
    private _triggerReelStopDelay:boolean = false;
    private _addReelStopDelay:boolean = false;
    private _startBlurringSymbols:boolean = false;
    private _showNormalSymbols:boolean = false;
    private _isGameDataParsed:boolean;
    private _blurInSymbols:boolean;
    private _possibleWinSymbols:string[];
    public _betWins:IAdjacentBetLineWinData[];
    private _isRestore:boolean;
    private _extraSpinStartDelay:number = 0;

    constructor(reelId:number) {
        super(reelId);
        this._isGameDataParsed = false;
    }

    protected onInitDataParsed(data:DirtyWildsParsedInitData):void {
        super.onInitDataParsed(data);
        this._isRestore = data.isRestoreState;
    }


    protected initReelStrip():void {
        super.initReelStrip();
        if(this._isRestore !== undefined && !this._isRestore) {
            EventHandler.dispatchEvent(new GameEvent(DirtyWildsRealAreaEvent.INIT_SYM_BEHAVIOUR, false));
        }
    }

    public triggerReelStopDelay(triggerNext:boolean) {
        this._triggerReelStopDelay = triggerNext;
    }

    public setSyncRFReelDelay(reelId:number, delay:number) {
        if(reelId !== this.reelId) {
            return;
        }
        this._addedStopDelay += delay;
    }

    /**
     * Extending base class function to add event listeners.
     */
    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, DirtyWildsRFSyncEvent.ADD_DIRTY_SYNC_DELAY, (event:GameEvent) => this.setSyncRFReelDelay(event.params[0], event.params[1]));
        EventHandler.addEventListener(this, DirtyWildsRandomFeatureEvent.ACTIVATE_SLOW_SPIN, (event:GameEvent) => this.activateSlowMotionSpin(event.params[0], event.params[1]));
        EventHandler.addEventListener(this, DirtyWildsReelController.ADD_SPIN_START_DELAY, (event:GameEvent) => this.addSpinStartDelay(event.params[0], event.params[1]));
    }

    private addSpinStartDelay(reelId:number, delay:number):void {
        if(this.reelId != reelId) {
            return;
        }
        this._extraSpinStartDelay = delay;
    }

    protected startSpinAnimation(data:IReelStartSpinData, previousDelay:number):number {
        previousDelay += this._extraSpinStartDelay;
        this._extraSpinStartDelay = 0;
        const delay = super.startSpinAnimation(data, previousDelay);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => this.moveAllSymbolsUnderMask(), delay);
        return delay;
    }

    private moveAllSymbolsUnderMask():void {
        (<DirtyWildsStateReelSymbol[]>this.allSymbols).forEach((symbol:DirtyWildsStateReelSymbol) => {
            if(symbol.parent) {
                if(symbol.symName === DirtyWildsSymbols.W || symbol.symName === DirtyWildsSymbols.W_STICKY) {
                    this.moveSymbolToMiddleLayer(symbol);
                } else {
                    this.moveSymbolToBottomLayer(symbol);
                }
            }
        });
    }

    /**
     * Function to capture game data
     * @param data
     */
    public onGameDataParsed(data:DirtyWildsParsedGameData):void {
        super.onGameDataParsed(data);
        this._isGameDataParsed = true;
        this._isRandomSyncFeatureTriggered = data.isRandomSyncFeatureTriggered;
        this._possibleWinSymbols = data.reels[1];
        this._isRandomFeatureTriggered = data.isRandomFeatureTriggered;
        this._betWins = <IAdjacentBetLineWinData[]>data.betWins;
        if(this._isRandomFeatureTriggered || this._isRandomSyncFeatureTriggered) {
            this._currentFullReelSet = this._currentFullReelSet.filter((a) => {return a !== "W"});
            this._currentReelSet = this._currentFullReelSet.concat();
        }
    }

    public activateSlowMotionSpin(endSpeed:number, steps:number):void {

        //Nearwin needs to be dispatched before the spinSpeed queue is setup!
        //EventHandler.dispatchEvent(new GameEvent(ReelEvent.FORCE_REEL_NEAR_WIN, 2));
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).isRandomFeatureReelStop = true;
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).currentReelId = this.reelId;
        if(this.reelId === 2) {
            this._spinReelPartAnimator.setupTweenSpinSpeedQueue(endSpeed, this._reelPartAnimator.isQuickStop ? 2 : steps);
            EventHandler.dispatchEvent(new GameEvent(DirtyWildsRandomFeatureEvent.RANDOM_QUICK_ANIMATE, this._reelPartAnimator.isQuickStop ? true : false));
            this._slowMoActive = true;
            const totalTimeForAllSlowdownSymbols = this._spinReelPartAnimator.spinSpeedsQueue.reduce((a, b) => a + b, 0);
            this._slowMoFadeOutDuration = this._spinReelPartAnimator.spinSpeedsQueue.filter(function(i, l) {
                return i < 0.085
            }).reduce((a, b) => a + b, 0);
            // this.dimEdgeReels();
            this._addedStopDelay += totalTimeForAllSlowdownSymbols;
            this._showNormalSymbols = true;
        }
    }

    public setDirtyWildRFSyncReelSpeed(reelId:number, endSpeed:number, steps:number, addReelStopDelay:boolean):number {
        this._showNormalSymbols = true;
        this._addReelStopDelay = addReelStopDelay;
        if(this.reelId === 0 || this.reelId === 4) {
            this._blurInSymbols = true;
        } else {
            this.setDirtySyncCurrentReelSet({
                reelSetName : "RANDOM_SYNC_REELSET",
                reelSetPosition : 100,
                reelId : this.reelId
            });
        }
        this._spinReelPartAnimator.setupTweenSpinSpeedQueue(endSpeed, this._reelPartAnimator.isQuickStop ? 2 : steps);
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).isRandomSyncReelStop = true;
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).currentReelId = this.reelId;

        const totalTimeForAllSlowdownSymbols = this._spinReelPartAnimator.spinSpeedsQueue.reduce((a, b) => a + b, 0);

        return totalTimeForAllSlowdownSymbols;
    }


    /**
     * Used to set a new reel set during spin. But it does NOT change the next reelSet which will be used when a new spin start.
     * Example : EventHandler.dispatchEvent(new GameEvent(ReelEvent.REEL_SET_CURRENT_REEL_SET, {reelSetName :
     * "BASE_REELSET_WILD", reelSetPosition: 10}));
     * If reelSetPosition is not given, use a random value.
     * @param data
     */
    protected setDirtySyncCurrentReelSet(data:{ reelSetName:string, reelSetPosition?:number, reelId:number }):void {
        if(this.reelId !== data.reelId) {
            return;
        }
        this._currentFullReelSet = Helper.getReelSet(data.reelSetName, this._reelId);
        this._currentReelSet = this._currentFullReelSet.concat();
        this._currentReelSetPosition = data.reelSetPosition == null ? this.randomizeReelSetPosition(this._currentReelSet) : data.reelSetPosition;
    }

    public deactivateSlowMotionSpin() {
        console.timeEnd("activateSlowMotionSpin");
        this._slowMoActive = false;
        this._slowMoFadeOutDuration = 0;
    }

    private dimEdgeReels() {
        const symbolsToDim:DirtyWildsStateReelSymbol[] = [];
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(0, 0));
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(0, 1));
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(0, 2));
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(4, 0));
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(4, 1));
        symbolsToDim.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(4, 2));

        for(let symbol of symbolsToDim) {
            if(symbol == null) {
                continue;
            }
            symbol.changeState({
                state : DirtyWildsGameConfig.instance.SYMBOL_STATES.randomObslete,
                fadeInDuration : this._slowMoFadeOutDuration
            })
        }
    }

    private highlightMatchingSymbols(centerSymbol:string, fadeInDuration:number, position1:number):boolean {
        const symbolsToCheck:DirtyWildsStateReelSymbol[] = [];

        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(1, 0));
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(1, 1));
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(1, 2));
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(3, 0));
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(3, 1));
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>Reels.getSymbol(3, 2));
        return this.addHighlightAnimation(centerSymbol, fadeInDuration, 0.2, symbolsToCheck, position1, 0, DirtyWildsGameConfig.instance.SYMBOL_STATES.lowlight);

    }

    private highlightMatchingSpinningSymbols(centerSymbol:string, fadeInDuration:number, position1:number):boolean {
        const symbolsToCheck:DirtyWildsStateReelSymbol[] = [];
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>this._reelPartAnimator.symbolsToAnimate[0]);
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>this._reelPartAnimator.symbolsToAnimate[1]);
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>this._reelPartAnimator.symbolsToAnimate[2]);
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>this._reelPartAnimator.symbolsToAnimate[3]);
        symbolsToCheck.push(<DirtyWildsStateReelSymbol>this._reelPartAnimator.symbolsToAnimate[4]);
        return this.addHighlightAnimation(centerSymbol, fadeInDuration, 0, symbolsToCheck, position1, 0.2, DirtyWildsGameConfig.instance.SYMBOL_STATES.normal);
    }

    private addHighlightAnimation(centerSymbol:string, fadeInDuration:number, fadeOutDuration:number, symbolsToCheck:DirtyWildsStateReelSymbol[], position1:number, position2:number, lowState:ISymbolStateConfig):boolean {
        let hasHighlight = false;
        for(let symbol of symbolsToCheck) {
            if(symbol == null) {
                continue;
            }
            let highTl:TimelineLite = new TimelineLite();
            if(symbol.symName == centerSymbol) {
                if(!hasHighlight) {
                    highTl.add(() => SlotGame.sound.playEffect(DirtyWildsSoundConfig.RANDOM_FEATURE_ATTENTION), position1);
                }
                hasHighlight = true;
                highTl.add(symbol.changeState({
                    state : DirtyWildsGameConfig.instance.SYMBOL_STATES.highlight,
                    fadeInDuration : fadeInDuration
                }), position1);
            } else {
                highTl.add(symbol.changeState({
                    state : lowState,
                    fadeInDuration : fadeOutDuration
                }), position2);
            }
        }

        return hasHighlight;
    }

    public getStopSymbols():DirtyWildsStateReelSymbol[] {
        return <DirtyWildsStateReelSymbol[]>this.allSymbols;
    }

    private checkAndUnblurSymbols(speedThreshold:number) {
        if((<any>this)._isSpinBlurred = false) {
            return;
        }
        if(this._spinReelPartAnimator.spinSpeedsQueue && this._spinReelPartAnimator.spinSpeedsQueue.length > 0 && this._spinReelPartAnimator.spinSpeedsQueue[0] > speedThreshold) {
            (<any>this)._isSpinBlurred = false;
        }


    }

    protected onSpinStepCompleted():void {
        super.onSpinStepCompleted();
        this.initiateRFSyncAnimation();

        if(!(this._slowMoActive)) {
            return;
        }
        let centerSymbol:string = "";
        let fadeInDuration:number = this._slowMoFadeOutDuration;
        this.checkAndUnblurSymbols(0.4);
        if(this._spinReelPartAnimator.currentSpeed > 0.4) {
            fadeInDuration = 0;
            centerSymbol = this._reelPartAnimator.symbolsToAnimate[2].symName;
            // SlotGame.sound.playEffect(DirtyWildsSoundConfig.SLOW_DOWN_TICK);
        }
        this.highlightMatchingSpinningSymbols(centerSymbol, fadeInDuration, this._spinReelPartAnimator.currentSpeed > 0.55 ? 0.45 : 0.30);
        this.highlightMatchingSymbols(centerSymbol, fadeInDuration, this._spinReelPartAnimator.currentSpeed > 0.55 ? 0.45 : 0.30);
    }

    private initiateRFSyncAnimation():void {
        if(this._isRandomSyncFeatureTriggered) {
            if(this._spinReelPartAnimator.spinSpeedsQueue && this._spinReelPartAnimator.spinSpeedsQueue.length === 1) {
                switch(this._spinReelPartAnimator.spinSpeedsQueue[0]) {
                    case 0.15:
                        EventHandler.dispatchEvent(new GameEvent(DirtyWildsRFSyncEvent.INIT_SPEED_ACHIEVED, this.reelId, 0.25, 5, this._addReelStopDelay, false));
                        break;
                    case 0.20:
                        EventHandler.dispatchEvent(new GameEvent(DirtyWildsRFSyncEvent.INIT_SPEED_ACHIEVED, this.reelId, 0.25, 20, this._addReelStopDelay, false));
                        break;
                }
            }
        }
    }

    public isQuickStop():boolean {
        return this._reelPartAnimator.isQuickStop || false;
    }

    protected completeSpin():void {

        this._showNormalSymbols = false;
        this._startBlurringSymbols = false;
        super.completeSpin();
    }

    public startSpin(data:IReelStartSpinData, previousDelay:number):number {
        this._startBlurringSymbols = false;
        this._blurInSymbols = false;
        this._isGameDataParsed = true;
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).isRandomSyncReelStop = false;
        (<DirtyWildReelPartAnimator>this._reelPartAnimator).isRandomFeatureReelStop = false;
        return super.startSpin(data, previousDelay);
    }

    protected makeUpStopReelSet(bottomFillUpTotalNum:number, init:boolean):string[] {
        let returnStopSet:string[] = super.makeUpStopReelSet(bottomFillUpTotalNum, init);
        returnStopSet.splice(returnStopSet.length - 1, 1);
        returnStopSet.push(this._stopReelSet[0]);
        this._startBlurringSymbols = true;
        return returnStopSet;
    }

    protected onSpinStopStarted():void {
        super.onSpinStopStarted();
        if(this._isRandomSyncFeatureTriggered) {
            this._isSpinBlurred = true;
        }
    }

    protected addNewSymbol(symName:string, state?:ISymbolStateConfig):StateReelSymbol {
        if(!this._isGameDataParsed) {
            this._isSpinBlurred = false;
            state = DirtyWildsGameConfig.instance.SYMBOL_STATES.wildGreen;
        } else if(this._showNormalSymbols) {
            this._isSpinBlurred = false;
            state = DirtyWildsGameConfig.instance.SYMBOL_STATES.normal;
        } else if(this._startBlurringSymbols && (this.reelId === 0 || this.reelId === 4) && this._isRandomFeatureTriggered) {
            this._isSpinBlurred = false;
            state = DirtyWildsGameConfig.instance.SYMBOL_STATES.randomObslete;
        }
        if(this._blurInSymbols && this._isRandomSyncFeatureTriggered) {
            this._isSpinBlurred = false;
            state = this._possibleWinSymbols.filter((a) => { return a === symName}).length > 0 ? state : DirtyWildsGameConfig.instance.SYMBOL_STATES.randomObslete;
        }
        const symbol:DirtyWildsStateReelSymbol = <DirtyWildsStateReelSymbol>super.addNewSymbol(symName, state);
        symbol.controller = this;
        return symbol;
    }

    public replaceWithNewSymbol(oldSymbol:StateReelSymbol, symName:string, deleteOld:boolean, state?:ISymbolStateConfig) {
        //Creates and adds it to the view
        const newSymbol = this.addNewSymbol(symName, state);
        newSymbol.x = oldSymbol.x;
        newSymbol.y = oldSymbol.y;

        const reelPartIndex = this._reelPartAnimator.symbolsToAnimate.indexOf(oldSymbol);
        this._reelPartAnimator.symbolsToAnimate[reelPartIndex] = newSymbol;

        const stopSymbolIndex = this.allSymbols.indexOf(oldSymbol);
        this.allSymbols[stopSymbolIndex] = newSymbol;

        if(deleteOld) {
            this.removeTheOldSymbol(oldSymbol);
        }
    }

    public liftSymbolOutOfReel(symbol:StateReelSymbol) {
        this.replaceWithNewSymbol(symbol, DirtyWildsSymbols.NONE, false);
    }

    public replaceSymbolWithSymbol(oldSymbol:StateReelSymbol, newSymbol:StateReelSymbol) {
        const reelPartIndex = this._reelPartAnimator.symbolsToAnimate.indexOf(oldSymbol);
        this._reelPartAnimator.symbolsToAnimate[reelPartIndex] = newSymbol;

        const stopSymbolIndex = this.allSymbols.indexOf(oldSymbol);
        this.allSymbols[stopSymbolIndex] = newSymbol;

        this.removeTheOldSymbol(oldSymbol)
    }

    public removeTheOldSymbol(oldSymbol:StateReelSymbol) {
        this._reelPartCreator.deleteSymbol(oldSymbol);
    }


    public moveSymbolToAttentionLayer(symbol:DirtyWildsStateReelSymbol) {
        (<DirtyWildReelView>this._reelView).attentionLayer.addChild(symbol);
    }

    public moveSymbolToTopLayer(symbol:DirtyWildsStateReelSymbol) {
        (<DirtyWildReelView>this._reelView).topLayer.addChild(symbol);
    }

    public removeSymbolFromTopLayer(symbol:DirtyWildsStateReelSymbol) {
        (<DirtyWildReelView>this._reelView).topLayer.removeChild(symbol);
    }

    public moveSymbolToMiddleLayer(symbol:DirtyWildsStateReelSymbol) {
        if(symbol.transform) {
            (<DirtyWildReelView>this._reelView).middleLayer.addChild(symbol);
        }
    }

    //Bottom layer is default
    public moveSymbolToBottomLayer(symbol:DirtyWildsStateReelSymbol) {
        if(symbol.transform) {
            (<DirtyWildReelView>this._reelView).bottomLayer.addChild(symbol);
        }
    }

    public moveSymbolToNoWinLayer(symbol:DirtyWildsStateReelSymbol) {
        (<DirtyWildReelView>this._reelView).noWinLayer.addChild(symbol);
    }

    public hasNearWin():boolean {
        if(this._isRandomFeatureTriggered) {
            return false;
        } else if(DirtyWildsModeData.instance.mode == DirtyWildGameMode.STICKY_RESPIN && [1, 2, 3].indexOf(this.reelId) > -1) {
            return true;
        } else {
            return super.hasNearWin();
        }
    }

    private isReSpinDelay():boolean {
        return (DirtyWildsModeData.instance.mode == DirtyWildGameMode.STICKY_RESPIN) && !this._reelPartAnimator.isQuickStop;
    }

    private getRandomFeatureStopDelays() {
        // if(this._doRandomQuickAnimate && reelId === 2){
        //     return 2 * this._randomFeatureQuickStopDelays[reelId];
        // }
        if(this._reelPartAnimator.isQuickStop) {
            return this._randomFeatureQuickStopDelays[this.reelId];
        }
        return this._randomFeatureStopDelays[this.reelId];
    }

    public stopSpin(stopData:IReelStopSpinData, previousStopTime:number):number {
        let delay:number = super.stopSpin(stopData, previousStopTime);

        if(this._isRandomSyncFeatureTriggered) {
            if(stopData.quickStop && (this._reelId === 1 || this._reelId === 2 || this._reelId === 3)) {
                delay = 0;
            } else if(this._reelId === 2 || this._reelId === 3) {
                delay = previousStopTime;
            }
        }

        return delay;
    }

    protected onStopDelayDone():void {
        EventHandler.dispatchEvent(new GameEvent(DirtyWildsRFSyncEvent.ANIMATE_SECOND_SET, this.reelId));

        let addDelayToNextReel:number = 0;
        const unfinishedSpinningStackedSymbolFillUp:string[] = this.handleUnfinishedSpinningStackedSymbols();
        this._currentReelSet = this.makeUpStopReelSet(GameConfig.instance.SYMBOLS_NUM_IN_REEL[4] + this._spinStopOffset - unfinishedSpinningStackedSymbolFillUp.length, false).concat(unfinishedSpinningStackedSymbolFillUp);
        this._currentReelSetPosition = this._currentReelSet.length;

        if(this._addedExtraStoppingFullStackedSymbolNum > 0) {
            // To add delay on the rest of reels. if there is any slow motion, it will be calculated in checkSlowMotionStopDelay().
            addDelayToNextReel += this._addedExtraStoppingFullStackedSymbolNum * this._spinReelPartAnimator.spinSpeed;
            this._spinStopOffset += this._addedExtraStoppingFullStackedSymbolNum;
        }
        this._stopCounter = this._symNumTotal + this._spinStopOffset;
        this._currentState = ReelAnimationState.STOP_SPIN;

        addDelayToNextReel += this.currentSpinStepLeftTime;

        if(addDelayToNextReel > 0 && !this._isRandomSyncFeatureTriggered) {
            EventHandler.dispatchEvent(new GameEvent(ReelEvent.REEL_ADD_STOP_DELAY, {
                reelId : this._reelId + 1,
                delay : addDelayToNextReel
            }));
        }
    }

    private getRandomSyncFeatureStopDelays():number {
        let delays:number[] = [6, -4.2, 0, 0, 4.5];
        if(this._reelPartAnimator.isQuickStop) {
            delays = [0.8, -0.4, 0, 0, 0.4];
        }

        return delays[this.reelId];
    }

    private getGameSpecificFeatureStopDelays(reelId:number, superFunction:Function):number {
        if(this._isRandomFeatureTriggered) {
            return this.getRandomFeatureStopDelays();
        } else if(this._isRandomSyncFeatureTriggered) {
            return this.getRandomSyncFeatureStopDelays();
        }
        if(this.isReSpinDelay()) {
            if(DirtyWildGameModuleConfig.reelAreaController.checkForOverLays(reelId)) {
                return 0;
            }
            return this._reSpinStopDelays[this.reelId];
        }

        return superFunction();
    }

    protected getNearWinStopDelay(reelId:number):number {
        if(DirtyWildGameModuleConfig.reelAreaController.checkForOverLays(reelId)) {
            return 0;
        }
        return this._reSpinStopDelays[reelId];
    }

    protected getNormalStopDelay(reelId:number):number {
        const delay:number = this.getGameSpecificFeatureStopDelays(reelId, () => super.getNormalStopDelay(reelId));
        Logger.logDev(`getNormalStopDelay reelId=${reelId}, delay=${delay}`);
        return delay;
    }

    protected getFastSpinStopDelay(reelId:number):number {
        const delay:number = this.getGameSpecificFeatureStopDelays(reelId, () => super.getNormalStopDelay(reelId) * 0.5);
        Logger.logDev(`getFastSpinStopDelay reelId=${reelId}, delay=${delay}`);
        return delay;
    }

    protected getQuickStopDelay(reelId:number):number {
        const delay:number = this.getGameSpecificFeatureStopDelays(reelId, () => super.getQuickStopDelay(reelId));
        Logger.logDev(`getQuickStopDelay reelId=${reelId}, delay=${delay}`);
        return delay;
    }
}